import { useRouter } from 'next/router';

import { useCallback, useEffect, useMemo, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import qs from 'qs';

import { LocalStorageKey } from '@nocowanie/core';
import { SearchEngineModel, useSearchCookie } from '@nocowanie/common-ui';

import { SearchCriteriaKeys } from '@app/enums';
import { environment } from '@app/environments/environment';
import { accommodationHelpers } from '@app/helpers';
import { useCrossStorage } from '@app/hooks';
import { QueryAccommodationArgs } from '@app/models/api/api-types';
import { accommodationSlice, useAppDispatch, useAppSelector } from '@app/state';

export const useAccommodationQueryParams = () => {
    const { replace } = useRouter();
    const [filtersFirstRender, setFiltersFirstRender] = useState(true);
    const [isInitialCriteriaSet, setIsInitialCriteriaSet] = useState(false);
    const [pageUIFirstRender, setPageUIFirstRender] = useState(true);
    const dispatch = useAppDispatch();
    const filtersS = useAppSelector(accommodationSlice.selectors.selectFilters);
    // Selectors seem to not memoize correctly so we have to do it ourselves.
    const filters = useMemo(
        () => filtersS,
        [
            filtersS?.id,
            filtersS?.checkIn,
            filtersS?.checkOut,
            filtersS?.childrenAges,
            filtersS?.childrenCount,
            filtersS?.guestsCount,
            filtersS?.flexDates,
        ],
    );
    const { setCookie } = useSearchCookie(environment.COOKIE_DOMAIN);

    const saveParamsToURL = useCallback(
        (params: Record<string, any>) => {
            const queryString = qs.stringify(params, { arrayFormat: 'indices' });
            const hash = window.location.hash;
            const url = `${window.location.pathname}?${queryString}${hash}`;
            replace(url, undefined, {
                shallow: true,
            });
        },
        [replace],
    );
    const crossStorageClient = useCrossStorage();
    const saveFiltersToCrossStorage = useCallback(async () => {
        if (!crossStorageClient) {
            return;
        }

        const queryString = await crossStorageClient.getItem(LocalStorageKey.SearchCriteria);

        const urlParams = new URLSearchParams(queryString);
        const instantReservation = urlParams.get(SearchCriteriaKeys.OnlineReservation) === '1';

        const params: Partial<QueryAccommodationArgs> & {
            instantReservation?: boolean;
            flexDates?: number;
        } = {
            ...filters,
            ...(instantReservation && {
                instantReservation,
            }),
        };

        const mappedParams = accommodationHelpers.generateLegacySearchCriteriaParams(params);

        crossStorageClient.setItem(LocalStorageKey.SearchCriteria, mappedParams).catch(error => {
            console.warn(error);
        });
    }, [filters, crossStorageClient]);

    useEffect(() => {
        // since we have SSR rendering/fetching we have to prevent refetch during client (re)render
        if (pageUIFirstRender) {
            setPageUIFirstRender(false);
            return;
        }

        const updatedParams = accommodationHelpers.updateParams(window.location.search);
        saveParamsToURL(updatedParams);
    }, []);

    const searchCriteria = useMemo(() => {
        return {
            dates: {
                checkInDate: filters?.checkIn ? new Date(filters.checkIn) : null,
                checkOutDate: filters?.checkOut ? new Date(filters.checkOut) : null,
            },
            guests: {
                adultsCount:
                    typeof filters?.guestsCount === 'number' ? filters.guestsCount : undefined,
                childrenCount:
                    typeof filters?.childrenCount === 'number' ? filters.childrenCount : 0,
                childrenAges: filters?.childrenAges
                    ? filters.childrenAges.filter((age): age is number => age !== null)
                    : undefined,
            },
            flexDates: typeof filters?.flexDates === 'number' ? filters.flexDates : undefined,
        } as SearchEngineModel;
    }, [filters]);

    const [prevSearchCriteria, setPrevSearchCriteria] = useState(cloneDeep(searchCriteria));

    useEffect(() => {
        // since we have SSR rendering/fetching we have to prevent refetch during client (re)render
        if (filtersFirstRender) {
            setFiltersFirstRender(false);
            return;
        }

        if (isEqual(prevSearchCriteria, searchCriteria)) {
            return;
        }

        const skipFetch = isEqualWith(
            searchCriteria,
            prevSearchCriteria,
            (value, prevValue, key) => {
                if (key === 'flexDates') {
                    return true;
                }
                return undefined;
            },
        );

        if (!skipFetch) {
            // Skip fetch if only `flexDates` param has changed
            dispatch(accommodationSlice.actions.fetchAccommodation());
        }

        const updatedParams = accommodationHelpers.updateParams(
            window.location.search,
            accommodationHelpers.mapFiltersToParams(filters as QueryAccommodationArgs),
        );

        saveParamsToURL(updatedParams);
        saveFiltersToCrossStorage();
        setCookie(searchCriteria);
        setPrevSearchCriteria(cloneDeep(searchCriteria));
    }, [filters, searchCriteria, setCookie, prevSearchCriteria]);

    // Save initial filters to CrossStorage when `CrossStorage` is initialized (after the first render)
    useEffect(() => {
        if (!crossStorageClient || isInitialCriteriaSet) {
            return;
        }
        saveFiltersToCrossStorage();
        setCookie(searchCriteria);
        setIsInitialCriteriaSet(true);
    }, [crossStorageClient, searchCriteria]);
};
